import { ArrowDown, ArrowUp, Plus } from "lucide-react";
import {
  CanAccess,
  useDataProvider,
  useGetList,
  useNotify,
  useRefresh,
  useTranslate,
} from "ra-core";
import { Link, Navigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
  resolveEffectiveDealStages,
  STAGE_CRITERIA_REGISTRY,
  type EffectiveDealStage,
} from "../deals/stageConfig";
import { ListCardsSkeleton, ListErrorState } from "../design/ListStates";
import { StatusPill } from "../design/StatusPill";
import type {
  DealActivityType,
  DealStageActivity,
  DealStageConfig,
  DealStageCriterion,
  StagePlacement,
} from "../types";
import {
  computeStageReorderSwap,
  resolveStageActivitySummary,
  resolveTargetTimeDisplay,
  toStageConfigErrorMessage,
} from "./stageConfigForm";

/** DATA CONTRACT EP (8): displayed group order, top to bottom. */
const GROUP_ORDER: StagePlacement[] = ["inbox", "kanban", "special"];

const GROUP_LABEL_KEYS: Record<StagePlacement, string> = {
  inbox: "resources.deal_stage_configs.group.inbox",
  kanban: "resources.deal_stage_configs.group.kanban",
  special: "resources.deal_stage_configs.group.special",
};

/**
 * Almikia Epic EP (TASK-105): "Etapas" -- direccion-only (mirrors
 * `IncentiveProgramList`'s `CanAccess` gate: every role reads the RLS-backed
 * data, only direccion may reach this screen at all). Groups the effective
 * stage list (TASK-102's shared resolver) into Bandeja / Tablero / Estados
 * especiales, each stage showing its label, target time, enabled criteria,
 * archived marker and its "Actividades de la etapa" (Epic EA, TASK-115: its
 * ENABLED-linked catalogue activities -- an archived one still shown, marked
 * 'Archivada' -- next to its EP free-text items), with "Subir"/"Bajar"
 * reorder controls inside Tablero and Estados especiales (Prospecto/Bandeja
 * never reorders -- it is the only 'inbox'-placement stage).
 */
export const DealStageConfigList = () => (
  <CanAccess
    resource="deal_stage_configs"
    action="edit"
    accessDenied={<Navigate to="/" replace />}
  >
    <DealStageConfigListContent />
  </CanAccess>
);

const DealStageConfigListContent = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const {
    data: configs,
    isPending: configsPending,
    error: configsError,
    refetch: refetchConfigs,
  } = useGetList<DealStageConfig>("deal_stage_configs", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "position", order: "ASC" },
  });
  const { data: criteria, isPending: criteriaPending } =
    useGetList<DealStageCriterion>("deal_stage_criteria", {
      pagination: { page: 1, perPage: 100 },
    });
  const { data: activityTypes, isPending: activityTypesPending } =
    useGetList<DealActivityType>("deal_activity_types", {
      pagination: { page: 1, perPage: 200 },
    });
  const { data: stageActivities, isPending: stageActivitiesPending } =
    useGetList<DealStageActivity>("deal_stage_activities", {
      pagination: { page: 1, perPage: 200 },
    });

  if (configsError) {
    return (
      <ListErrorState
        message={translate("crm.common.list_error")}
        onRetry={() => refetchConfigs()}
      />
    );
  }
  if (
    configsPending ||
    criteriaPending ||
    activityTypesPending ||
    stageActivitiesPending ||
    !configs ||
    !criteria ||
    !activityTypes ||
    !stageActivities
  ) {
    return <ListCardsSkeleton rows={10} />;
  }

  const idByKey = new Map(configs.map((config) => [config.key, config.id]));
  const effectiveStages = resolveEffectiveDealStages(configs);
  const groups: Record<StagePlacement, EffectiveDealStage[]> = {
    inbox: [],
    kanban: [],
    special: [],
  };
  for (const stage of effectiveStages) {
    groups[stage.placement].push(stage);
  }

  const handleReorder = async (
    group: EffectiveDealStage[],
    value: string,
    direction: "up" | "down",
  ) => {
    const swap = computeStageReorderSwap(group, value, direction, (key) =>
      idByKey.get(key),
    );
    if (!swap) return;
    try {
      await Promise.all(
        swap.map((update) =>
          dataProvider.update("deal_stage_configs", {
            id: update.id,
            data: { position: update.position },
            previousData: configs.find((config) => config.id === update.id),
          }),
        ),
      );
      refresh();
    } catch (error) {
      notify(toStageConfigErrorMessage(error), { type: "error" });
    }
  };

  const enabledCriteriaLabel = (stageKey: string): string => {
    const enabledCodes = criteria
      .filter(
        (criterion) => criterion.stage_key === stageKey && criterion.enabled,
      )
      .map(
        (criterion) => STAGE_CRITERIA_REGISTRY[criterion.criterion_code].label,
      );
    return enabledCodes.length > 0
      ? enabledCodes.join(", ")
      : translate("resources.deal_stage_configs.criteria.none");
  };

  /**
   * Epic EA (TASK-115): the stage's free-text `required_activities` items
   * plus its ENABLED-linked catalogue activities, an archived one marked
   * 'Archivada' -- displayed together under the same "Actividades de la
   * etapa" label the edit form already uses.
   */
  const stageActivitiesLabel = (stage: EffectiveDealStage): string => {
    const catalogueNames = resolveStageActivitySummary(
      stage.value,
      activityTypes,
      stageActivities,
    ).map((item) =>
      item.archived
        ? `${item.name} (${translate("resources.deal_stage_configs.activities.archived_marker")})`
        : item.name,
    );
    const allItems = [...catalogueNames, ...stage.requiredActivities];
    return allItems.length > 0
      ? allItems.join(", ")
      : translate("resources.deal_stage_configs.activities.none");
  };

  return (
    <div className="mt-2 max-w-2xl mx-auto space-y-6 pb-8">
      <div className="flex items-center justify-between px-1">
        <h1 className="text-xl font-semibold">
          {translate("resources.deal_stage_configs.title")}
        </h1>
        <Button asChild size="sm" className="min-h-11">
          <Link to="/deal_stage_configs/create">
            <Plus className="size-4 mr-1" />
            {translate("resources.deal_stage_configs.action.new_stage")}
          </Link>
        </Button>
      </div>

      {GROUP_ORDER.map((placement) => {
        const group = groups[placement];
        if (group.length === 0) return null;
        return (
          <section key={placement} className="space-y-2">
            <h2 className="text-sm font-medium text-muted-foreground px-1">
              {translate(GROUP_LABEL_KEYS[placement])}
            </h2>
            <Card>
              <CardContent className="divide-y p-0">
                {group.map((stage, index) => {
                  const targetTime = resolveTargetTimeDisplay(
                    stage.targetHours,
                  );
                  const targetTimeText =
                    targetTime.unit === "none"
                      ? translate(
                          "resources.deal_stage_configs.target_time.none",
                        )
                      : targetTime.unit === "hours"
                        ? translate(
                            "resources.deal_stage_configs.target_time.hours",
                            { smart_count: targetTime.value ?? 0 },
                          )
                        : translate(
                            "resources.deal_stage_configs.target_time.days",
                            { smart_count: targetTime.value ?? 0 },
                          );
                  const canReorder = placement !== "inbox";

                  return (
                    <div
                      key={stage.value}
                      className="flex items-center gap-3 p-4"
                    >
                      <Link
                        to={`/deal_stage_configs/${idByKey.get(stage.value)}`}
                        className="flex-1 min-w-0"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium truncate">
                            {stage.label}
                          </span>
                          {stage.isSystem ? (
                            <StatusPill
                              tone="ok"
                              text={translate(
                                "resources.deal_stage_configs.system_marker",
                              )}
                            />
                          ) : null}
                          {stage.archived ? (
                            <StatusPill
                              tone="not-ok"
                              text={translate(
                                "resources.deal_stage_configs.archived_marker",
                              )}
                            />
                          ) : null}
                        </div>
                        <p className="text-sm text-muted-foreground tabular-nums">
                          {targetTimeText} · {enabledCriteriaLabel(stage.value)}
                        </p>
                        <p className="text-xs text-muted-foreground line-clamp-3 break-words">
                          {translate(
                            "resources.deal_stage_configs.fields.required_activities",
                          )}
                          : {stageActivitiesLabel(stage)}
                        </p>
                      </Link>
                      {canReorder ? (
                        <div className="flex flex-col gap-1 shrink-0">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="size-11"
                            disabled={index === 0}
                            aria-label={`${translate(
                              "resources.deal_stage_configs.action.move_up",
                            )} ${stage.label}`}
                            onClick={() =>
                              handleReorder(group, stage.value, "up")
                            }
                          >
                            <ArrowUp className="size-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="size-11"
                            disabled={index === group.length - 1}
                            aria-label={`${translate(
                              "resources.deal_stage_configs.action.move_down",
                            )} ${stage.label}`}
                            onClick={() =>
                              handleReorder(group, stage.value, "down")
                            }
                          >
                            <ArrowDown className="size-4" />
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </section>
        );
      })}
    </div>
  );
};
